import { LicenseInfo } from "@mui/x-license-pro";

/**
 * 1F SDK (formerly OneFront)
 * ============
 *
 * This package provides most of the Services that you may need
 * in a OnePlatform Application.
 *
 * You write simple React Components and interact with the SDK
 * using modern hooks API.
 *
 * The SDK guides you into dividing you App into isolated Features
 * that you will glue together using ForrestJS APIs.
 */
import {OneFront} from "@1f/react-sdk";
//import "./i18n";

import {
  fetchOfflinePageContent,
  showOfflineModal,
} from "./businessLogic/helpers/offlinePage";

// Intervallo del monitoraggio runtime della pagina offline (2 minuti).
const OFFLINE_PAGE_CHECK_INTERVAL_MS = 120000;

/**
 * Import the App's Features
 * =========================
 *
 * A Feature is an isolated portion of business logic that
 * interacts with the App's Services
 */

import { dashboard } from "./features/dashboard";
import { azienda } from "./features/azienda";
import { fascicolo } from "./features/fascicolo";
import { anagrafica } from "./features/anagrafica";
import { prestazione } from "./features/prestazione";
import { rischio } from "./features/rischio";
import { autovalutazione } from "./features/autovalutazione";
import { segnalazione } from "./features/segnalazione";
import { normativa } from "./features/normativa";
import { impostazione } from "./features/impostazione";
import { exportDati } from "./features/exportDati";

/**
 * Configure and Run the App
 * =========================
 *
 * The boot process returns a Promise that you can handle
 * to figure out whether all was good or not.
 */
import "./components/anagrafica/AnagraficaFormStyles.css";
//const appId = import.meta.env.VITE_APP_ID || "TS420";
const appId = import.meta.env.VITE_APP_ID || "demo";
const proxyBaseUrl = import.meta.env.VITE_PROXY_BASE_URL || "/api"


// Some legacy dependencies expect a Node-like `process` global in the browser.
if (typeof globalThis.process === "undefined") {
  globalThis.process = { env: {} };
} else if (typeof globalThis.process.env === "undefined") {
  globalThis.process.env = {};
}

if (typeof globalThis.process.env.NODE_ENV === "undefined") {
  globalThis.process.env.NODE_ENV = import.meta.env.MODE;
}

// MUI License Datagrid PRO
const muiLicenseKey =
  __MUI_LICENSE_KEY__ ||
  import.meta.env.VITE_MUI_LICENCE_KEY ||
  import.meta.env.VITE_MUI_LICENSE_KEY ||
  import.meta.env.REACT_APP_MUI_LICENCE_KEY ||
  import.meta.env.REACT_APP_MUI_LICENSE_KEY;

if (muiLicenseKey) {
  LicenseInfo.setLicenseKey(muiLicenseKey);
}

/**
 * Se la pagina di manutenzione è pubblicata sulla CDN, ne scarica il contenuto
 * e lo mostra in una modale bloccante a tutto schermo.
 *
 * Trigger unico: la presenza del file offline.html sulla CDN (HTTP 200) segnala
 * una manutenzione attivata manualmente dal team. In quel caso la modale blocca
 * l'app e nient'altro viene caricato.
 *
 * @returns {Promise<boolean>} true se la modale è stata mostrata (app da fermare)
 */
async function blockIfOffline() {
  const htmlContent = await fetchOfflinePageContent();
  if (htmlContent !== null) {
    return showOfflineModal(htmlContent);
  }
  return false;
}

/**
 * Avvia il monitoraggio periodico durante l'uso dell'app.
 * Se compare offline.html sulla CDN, mostra la modale bloccante.
 */
function startOfflinePageMonitor() {
  const intervalId = setInterval(async () => {
    const blocked = await blockIfOffline();
    if (blocked) {
      clearInterval(intervalId);
    }
  }, OFFLINE_PAGE_CHECK_INTERVAL_MS);
}

/**
 * Boot dell'applicazione con controllo di stato pre-avvio.
 * 1. Se in manutenzione (offline.html pubblicata su CDN) → modale bloccante, l'app non parte
 * 2. Altrimenti → avvia OneFront normalmente + monitor runtime
 */
async function boot() {
  if (await blockIfOffline()) {
    return;
  }

  OneFront.run({
    trace:"compact",
    settings:{    
      one:{
        appId,
        axios:{
          proxy:{
            baseUrl:proxyBaseUrl
          }
        },
        auth:{
          token:{
            verify:true,
            refresh:true
          }
        },
        layout:{
          title:"TeamSystem Antiriciclaggio",
          embed:{
            param:{
              key:false
            }
          }
        },
        i18n:{
          options:{
            ns:["react-sdk", "antiriciclaggio"],
            defaultNS:"antiriciclaggio",
            fallbackLng:"it",
            expirationTime:30000
          }
        },
        rest:{
          graphQL:{
            baseUrl:"aml:hasura:api://v1/graphql"
          }
        },
        gainsight:{
          enabled:false
        }     
      }
    },
    features:[    
      dashboard,
      azienda,
      fascicolo,
      anagrafica,
      prestazione,
      rischio,
      autovalutazione,
      segnalazione,
      normativa,
      impostazione,
      exportDati
    ]
  })
  .then(() => {
    startOfflinePageMonitor();
  })
  .catch(console.error);
}

boot();
